import { useCallback } from "react";
import useSWRInfinite from "swr/infinite";
import { mutate as globalMutate } from "swr";
import { getLeadTimeline, clearTimelineServiceCache } from "../services/leadsService";

const DEFAULT_PAGE_SIZE = 20;

/**
 * Generate a cache key for SWR infinite scroll
 * @param {string|number} leadId - The lead ID
 * @param {number} page - Page number
 * @param {number} pageSize - Number of items per page
 * @returns {Array} - Cache key array
 */
export function getTimelineCacheKey(leadId, jobId, page, pageSize) {
  return ["timeline", leadId || "null", jobId || "null", page, pageSize];
}

export function invalidateTimelineCache(leadId = null, jobId = null) {
  if (Array.isArray(leadId)) {
    leadId.forEach((id) => clearTimelineServiceCache(id));
  } else {
    clearTimelineServiceCache(leadId);
  }
  if (Array.isArray(jobId)) {
    jobId.forEach((id) => clearTimelineServiceCache(id));
  } else if (jobId) {
    clearTimelineServiceCache(jobId);
  }
  const targetLeadIds = Array.isArray(leadId)
    ? leadId.map((id) => String(id).toLowerCase().trim()).filter(Boolean)
    : leadId
    ? [String(leadId).toLowerCase().trim()]
    : null;

  const targetJobIds = Array.isArray(jobId)
    ? jobId.map((id) => String(id).toLowerCase().trim()).filter(Boolean)
    : jobId
    ? [String(jobId).toLowerCase().trim()]
    : null;

  return globalMutate(
    (key) => {
      if (typeof key === "string") {
        if (!key.toLowerCase().includes("timeline")) return false;
        const matchesLead =
          !targetLeadIds || targetLeadIds.some((lid) => key.toLowerCase().includes(lid));
        const matchesJob =
          !targetJobIds || targetJobIds.some((jid) => key.toLowerCase().includes(jid));
        return matchesLead && matchesJob;
      }
      if (Array.isArray(key) && key[0] === "timeline") {
        const keyLeadId = String(key[1] ?? "").toLowerCase().trim();
        const keyJobId = String(key[2] ?? "").toLowerCase().trim();

        const matchesLead =
          !targetLeadIds || targetLeadIds.includes(keyLeadId) || keyLeadId === "null";
        const matchesJob =
          !targetJobIds || targetJobIds.includes(keyJobId) || keyJobId === "null";
        return matchesLead && matchesJob;
      }
      return false;
    },
    undefined,
    { revalidate: true }
  );
}

/**
 * Custom hook to manage lead timeline activities with pagination and global caching
 * @param {string|number} leadId - The lead ID
 * @param {number} pageSize - Number of items per page
 * @param {string|number} jobId - The job ID
 * @returns {Object} - { activities, totalRecords, isLoading, isValidating, hasMore, error, loadMore, refresh, mutate }
 */
export function useTimeline(leadId = null, pageSize = DEFAULT_PAGE_SIZE, jobId = null) {
  const getKey = (pageIndex, previousPageData) => {
    if (!leadId && !jobId) return null;

    // If reached the end of the list
    if (previousPageData && (!previousPageData.data || previousPageData.data.length === 0)) {
      return null;
    }

    return getTimelineCacheKey(leadId, jobId, pageIndex + 1, pageSize);
  };

  const { data, error, size, setSize, isValidating, isLoading, mutate } = useSWRInfinite(
    getKey,
    async ([, lId, jId, page, sizeLimit]) => {
      // Force refresh is set to true so that SWR handles the caching and we get fresh data
      const result = await getLeadTimeline(lId === "null" ? null : lId, page, sizeLimit, true, jId === "null" ? null : jId);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to fetch timeline");
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const activities = data ? data.flatMap((page) => page.data || []) : [];
  const totalRecords = data?.[0]?.totalRecords || 0;

  // Calculate if there are more items to load
  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage
    ? lastPage.data && lastPage.data.length === pageSize && activities.length < totalRecords
    : false;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) {
      setSize((prevSize) => prevSize + 1);
    }
  }, [isValidating, hasMore, setSize]);

  const refresh = useCallback(async () => {
    await setSize(1);
    mutate();
  }, [setSize, mutate]);

  return {
    activities,
    totalRecords,
    isLoading: isLoading || (size > 1 && isValidating && activities.length === 0),
    isValidating,
    hasMore,
    error,
    loadMore,
    refresh,
    mutate,
  };
}
